import React, { useState, useEffect, useCallback } from 'react';
import { Trash2, Users, BarChart2, LogOut, ChevronLeft, Search, AlertTriangle, CheckCircle, ImageOff, SlidersHorizontal, Download, ClipboardList, FlaskConical, FileText } from 'lucide-react';
import html2pdf from 'html2pdf.js';
import { supabase } from './supabaseClient';
import { useAuth } from './AuthContext';
import { StorageImage } from './StorageImage';
import { ImageZoomOverlay } from './ImageZoomOverlay';
import RegressionTestTab from './RegressionTestTab';
import { DateRangePicker } from './DateRangePicker';
import { exportCSV, formatSerial, isIOS as isIOSDevice } from './utils/format';
import { IMAGE_MODE_LABEL, IMAGE_MODE_STYLE, FEEDBACK_LABEL, FEEDBACK_STYLE } from './constants';
import { runWithConcurrency } from './utils/concurrencyPool';
import { openBlankTab, openTabWithContent } from './utils/blankTab';
import { usePendingPdfShare } from './utils/pdfShare';
import { downscaleForPdf } from './utils/pdfImage';
import { AUTO_PRINT_SCRIPT } from './utils/printAutoScript';
import { isBlurryImage, blurryImageBadgeHtml, BLURRY_IMAGE_LABEL } from './utils/imageSharpness';
import { jstMonthStart } from './utils/jstMonth';

async function downloadInspectionImage(path, serial, index) {
  const { data, error } = await supabase.storage
    .from('pm-inspection-images')
    .download(path);
  if (error || !data) return;
  const ext = path.split('.').pop() || 'jpg';
  const filename = `PM_${formatSerial(serial)}_${index}.${ext}`;
  const url = URL.createObjectURL(data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// 照合結果カード1件分（判定バッジ＋詳細テーブル）のHTMLを構築する。
// openInspectionPdf（個別）・openBulkInspectionPdf（検索結果全件）の両方から共用する。
// このファイル（CompanyAdmin向け）では使用モデルは表示しない（SuperAdminPanel.js側の
// 同名関数は表示する）。ユーザー指示により意図的に除外している
function buildInspectionCardHtml(insp, imgHtml = '') {
  const productName = insp.label_product_name || '—';
  const serial = formatSerial(insp.serial);
  const status = insp.match_status || 'SAFE';
  const esc = (s) => String(s ?? '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  const color = status === 'SAFE' ? '#2563eb' : status === 'DANGER' ? '#dc2626' : '#6b7280';
  const label = status === 'SAFE' ? '照合OK' : status === 'DANGER' ? '要確認' : 'エラー';

  const tdH = 'padding:3px 8px;background:#f8fafc;font-weight:bold;width:80px;border-bottom:1px solid #e2e8f0;font-size:10px';
  const tdV = 'padding:3px 8px;border-bottom:1px solid #e2e8f0;font-size:10px';
  let detailHtml = `<table style="width:100%;border-collapse:collapse;border:1px solid #e2e8f0"><tbody>`;
  detailHtml += `<tr><td style="${tdH}">商品名</td><td style="${tdV}">${esc(productName)}</td></tr>`;
  if (insp.detected_food_visual) detailHtml += `<tr><td style="${tdH}">目視識別</td><td style="${tdV}">${esc(insp.detected_food_visual)}</td></tr>`;
  if (insp.reason) detailHtml += `<tr><td style="${tdH}">判定理由</td><td style="${tdV}">${esc(insp.reason)}</td></tr>`;
  if (insp.lot_number) detailHtml += `<tr><td style="${tdH}">ロット</td><td style="${tdV}">${esc(insp.lot_number)}</td></tr>`;
  detailHtml += `<tr><td style="${tdH}">店舗/部門</td><td style="${tdV}">${esc(insp.store_name || '—')} / ${esc(insp.dept_name || '—')}</td></tr>`;
  if (insp.image_mode) detailHtml += `<tr><td style="${tdH}">検査モード</td><td style="${tdV}">${insp.image_mode === 'speed' ? '⚡速度優先' : insp.image_mode === 'accuracy' ? '🔍精度優先' : esc(insp.image_mode)}</td></tr>`;
  if (insp.device_type || insp.browser_type) detailHtml += `<tr><td style="${tdH}">端末</td><td style="${tdV}">${[insp.device_type, insp.browser_type].filter(Boolean).map(esc).join(' / ')}</td></tr>`;
  if (insp.is_standalone != null) detailHtml += `<tr><td style="${tdH}">動作モード</td><td style="${tdV}">${insp.is_standalone ? 'ホーム画面から起動' : 'ブラウザで表示'}</td></tr>`;
  detailHtml += `<tr><td style="${tdH}">検査日時</td><td style="${tdV}">${insp.inspected_at ? new Date(insp.inspected_at).toLocaleString('ja-JP') : '—'}</td></tr>`;
  detailHtml += `<tr><td style="${tdH}">担当者</td><td style="${tdV}">${esc(insp.user_email || '—')}</td></tr>`;
  detailHtml += `</tbody></table>`;

  return `
<div style="page-break-inside:avoid;border:1px solid #e2e8f0;border-radius:8px;padding:12px;margin-bottom:12px;background:#fff">
<div style="margin-bottom:8px;display:flex;align-items:center;gap:8px">
${serial ? `<span style="font-size:10px;color:#64748b;font-family:monospace">${serial}</span>` : ''}
<span style="display:inline-block;padding:3px 10px;border-radius:4px;font-size:12px;font-weight:bold;color:#fff;background:${color}">${label}</span>${blurryImageBadgeHtml(insp.image_quality)}
<span style="font-size:14px;font-weight:bold">${esc(productName)}</span></div>
<div style="display:flex;gap:12px;margin-bottom:8px">${imgHtml ? `<div style="flex:0 0 200px">${imgHtml}</div>` : ''}
<div style="flex:1;min-width:0">${detailHtml}</div></div>
</div>`;
}

// insp.image_urls（Storage上のパス配列）をダウンロードしてbase64データURLの配列に変換する。
// openInspectionPdf（個別）・openBulkInspectionPdf（検索結果全件）の両方から共用する
async function fetchInspectionImages(insp) {
  const images = [];
  for (const path of (insp.image_urls || [])) {
    const { data } = await supabase.storage.from('pm-inspection-images').download(path);
    if (data) {
      const b64 = await new Promise(resolve => { const r = new FileReader(); r.onloadend = () => resolve(r.result); r.readAsDataURL(data); });
      images.push(b64);
    }
  }
  return images;
}

// 表示は max-width:200px のため、長辺500px程度あれば十分（html2canvasのscale:2を
// 踏まえても余裕がある）。フル解像度のまま埋め込むとiPhone SEでの生成が遅くなる
// （姉妹アプリfood-label-assistで見つかった不具合と同じ対策。PR#138参照）
async function imagesToHtml(images) {
  const small = await Promise.all(images.map((img) => downscaleForPdf(img, 500)));
  return small.map(b64 => `<img src="${b64}" style="width:100%;max-width:200px;object-fit:contain;border-radius:6px;border:1px solid #e2e8f0;margin-bottom:4px" />`).join('');
}

// onIOSPdfReady(blob, filename): iOSでPDF生成が終わった直後に呼ばれるコールバック。
// 呼び出し側（コンポーネント）は usePendingPdfShare の shareOrDefer を渡す。
// ユーザーアクティベーションが生きていればそのまま共有シートへ進み、
// 切れていた場合だけ「PDFを保存」ボタンに退避される（usePendingPdfShare参照）
async function openInspectionPdf(insp, { exporterEmail = '―', onIOSPdfReady } = {}) {
  const productName = insp.label_product_name || '—';
  const serial = formatSerial(insp.serial);
  const esc = (s) => String(s ?? '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const dateStr = `${now.getFullYear()}/${pad(now.getMonth()+1)}/${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}`;

  // iOSはhtml2pdf.js直接ダウンロード、非iOSは画像ダウンロード（下のawait）より前に
  // タブを開いておく（openBlankTab参照）
  const isIOS = isIOSDevice();
  const w = isIOS ? null : openBlankTab();
  if (!isIOS && !w) return;

  const images = await fetchInspectionImages(insp);
  const cardHtml = buildInspectionCardHtml(insp, await imagesToHtml(images));

  const html = `<!DOCTYPE html><html lang="ja"><head><meta charset="UTF-8"><title>照合結果 ${esc(productName)}</title>
<style>body{font-family:'Hiragino Sans','Meiryo',sans-serif;padding:20px;font-size:11px;color:#1e293b;background:#f8fafc}@media print{@page{margin:10mm}body{background:#fff}html{-webkit-print-color-adjust:exact;print-color-adjust:exact}}</style>
</head><body>
<div style="margin-bottom:10px"><h2 style="font-size:16px;margin:0;color:#4f46e5">ProductMatcher 照合レポート</h2>
<span style="font-size:11px;color:#64748b">${serial ? serial + ' / ' : ''}出力者: ${esc(exporterEmail)} / 出力: ${dateStr}</span></div>
${cardHtml}
${AUTO_PRINT_SCRIPT}</body></html>`;

  if (isIOS) {
    const printHtml = html.replace(/<script>.*?<\/script>/g, '');
    const worker = html2pdf().set({
      margin: 10,
      image: { type: 'jpeg', quality: 0.95 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
    }).from(printHtml, 'string');
    const blob = await worker.outputPdf('blob');
    await onIOSPdfReady(blob, `PM_照合結果_${serial || ''}${productName}`);
    return;
  }
  openTabWithContent(w, html);
}

// 検査履歴タブの検索結果を全件まとめて1つのPDFにする。個別PDFと同様、各検査の
// 画像も含める。検索上限は最大100件だが、無制限のPromise.allで全件を同時に
// ダウンロードするとSupabaseへの同時リクエストが膨大になるため、runWithConcurrency
// （App.js等でも使用する既存ユーティリティ）で同時実行数を制限する
async function openBulkInspectionPdf(inspections, { exporterEmail = '―', onIOSPdfReady } = {}) {
  if (!inspections || inspections.length === 0) return;
  const esc = (s) => String(s ?? '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');

  // iOSはhtml2pdf.js直接ダウンロード、非iOSは画像ダウンロード（下のrunWithConcurrency、
  // 検索結果が多いと数秒〜十数秒かかりうる）より前にタブを開いておく（openBlankTab参照）
  const isIOS = isIOSDevice();
  const w = isIOS ? null : openBlankTab();
  if (!isIOS && !w) return;

  const settled = await runWithConcurrency(inspections, insp => fetchInspectionImages(insp), { concurrency: 4 });

  const cardsHtml = (await Promise.all(settled.map(async entry => {
    const insp = entry.item;
    const images = entry.status === 'fulfilled' ? entry.value : [];
    return buildInspectionCardHtml(insp, await imagesToHtml(images));
  }))).join('');

  const safeCount = inspections.filter(i => i.match_status === 'SAFE').length;
  const dangerCount = inspections.filter(i => i.match_status === 'DANGER').length;
  const errorCount = inspections.filter(i => i.match_status === 'ERROR').length;

  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const dateStr = `${now.getFullYear()}/${pad(now.getMonth()+1)}/${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}`;
  const fileDate = `${now.getFullYear()}${pad(now.getMonth()+1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}`;
  const filename = `PM_検査履歴一覧_${fileDate}`;

  const html = `<!DOCTYPE html><html lang="ja"><head><meta charset="UTF-8"><title>${filename}</title>
<style>body{font-family:'Hiragino Sans','Meiryo',sans-serif;padding:20px;font-size:11px;color:#1e293b;background:#f8fafc}@media print{@page{margin:10mm}body{background:#fff}html{-webkit-print-color-adjust:exact;print-color-adjust:exact}}</style>
</head><body>
<div style="margin-bottom:10px">
<h2 style="font-size:16px;margin:0;color:#4f46e5">ProductMatcher 検査履歴一覧</h2>
<div style="font-size:11px;color:#64748b;margin-top:2px">出力者: ${esc(exporterEmail)} / 出力: ${dateStr}</div>
<div style="margin-top:4px;font-size:11px">合計: <b>${inspections.length}件</b>　<span style="color:#2563eb">照合OK: <b>${safeCount}件</b></span>　<span style="color:#dc2626">要確認: <b>${dangerCount}件</b></span>　${errorCount > 0 ? `<span style="color:#6b7280">エラー: <b>${errorCount}件</b></span>` : ''}</div>
</div>
${cardsHtml}
${AUTO_PRINT_SCRIPT}</body></html>`;

  if (isIOS) {
    const printHtml = html.replace(/<script>.*?<\/script>/g, '');
    const worker = html2pdf().set({
      margin: 10,
      image: { type: 'jpeg', quality: 0.95 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
    }).from(printHtml, 'string');
    const blob = await worker.outputPdf('blob');
    await onIOSPdfReady(blob, filename);
    return;
  }
  openTabWithContent(w, html);
}

async function saveCompanyField(companyId, field, value) {
  const { data: { session } } = await supabase.auth.getSession();
  const res = await fetch('/api/update-company', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
    body: JSON.stringify({ companyId, field, value }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) { alert(data.error || '保存に失敗しました'); return false; }
  return true;
}

function RegisterReferenceButton({ inspection, companyId }) {
  const { profile } = useAuth();
  const [showForm, setShowForm] = useState(false);
  const defaultLabel = (() => {
    const date = inspection.inspected_at ? new Date(inspection.inspected_at).toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\//g, '') : '';
    const product = inspection.label_product_name || '';
    return [date, product].filter(Boolean).join('_');
  })();
  const [label, setLabel] = useState(defaultLabel);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const handleSave = async () => {
    if (!label.trim()) return;
    setSaving(true);
    const { error } = await supabase.from('pm_reference_labels').insert({
      company_id: companyId,
      inspection_id: inspection.id || null,
      expected_match_status: inspection.match_status || null,
      store_name: inspection.store_name || null,
      dept_name: inspection.dept_name || null,
      image_mode: inspection.image_mode || null,
      label: label.trim(),
      created_by: profile?.email,
    });
    setSaving(false);
    if (!error) { setDone(true); setShowForm(false); }
    else if (error.code === '23505') { alert('この検査は既に基準登録済みです'); setShowForm(false); }
    else alert('保存エラー: ' + error.message);
  };

  if (done) return <p className="text-[10px] text-green-600 mt-1">基準ラベル登録済み</p>;

  return (
    <div className="mt-1">
      {!showForm ? (
        <button onClick={() => setShowForm(true)} className="text-[10px] px-2 py-1 rounded bg-purple-50 text-purple-700 hover:bg-purple-100 font-medium">
          基準ラベルとして登録
        </button>
      ) : (
        <div className="bg-purple-50 rounded p-2 space-y-1.5">
          <input type="text" placeholder="ラベル名" value={label} onChange={e => setLabel(e.target.value)} className="w-full px-2 py-1 text-xs border rounded" autoFocus />
          <div className="flex gap-2">
            <button onClick={handleSave} disabled={saving || !label.trim()} className="flex-1 py-1 text-[10px] font-bold bg-purple-600 text-white rounded disabled:bg-slate-300">
              {saving ? '保存中...' : '登録'}
            </button>
            <button onClick={() => setShowForm(false)} className="flex-1 py-1 text-[10px] bg-slate-200 text-slate-600 rounded">キャンセル</button>
          </div>
        </div>
      )}
    </div>
  );
}

const DEPARTMENT_KEYS = ['青果', '精肉', '鮮魚', '惣菜', 'ベーカリー'];

function DepartmentLabelsEditor({ companyId, currentLabels, onSaved, canEdit }) {
  const [labels, setLabels] = useState(currentLabels || {});
  const [saving, setSaving] = useState(false);

  useEffect(() => { setLabels(currentLabels || {}); }, [currentLabels]);

  const setLabel = (dept, value) => {
    setLabels(prev => {
      const next = { ...prev };
      const trimmed = value.trim();
      if (trimmed === '') delete next[dept];
      else next[dept] = trimmed;
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    const ok = await saveCompanyField(companyId, 'department_labels', Object.keys(labels).length > 0 ? labels : null);
    setSaving(false);
    if (ok) onSaved?.();
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-500">部門名の表示をカスタマイズできます。変更しない部門は空欄のままにしてください。</p>
      <div className="border rounded-lg overflow-hidden divide-y">
        {DEPARTMENT_KEYS.map(dept => (
          <div key={dept} className="flex items-center justify-between px-3 py-2 gap-2">
            <span className="text-xs text-slate-700 w-20 shrink-0">{dept}</span>
            <input
              type="text" maxLength={20}
              placeholder={dept}
              value={labels[dept] ?? ''}
              onChange={e => setLabel(dept, e.target.value)}
              disabled={!canEdit}
              className="flex-1 px-2 py-1 border rounded text-xs disabled:bg-slate-50"
            />
          </div>
        ))}
      </div>
      {canEdit && (
        <button onClick={save} disabled={saving} className="w-full py-2 bg-slate-700 text-white text-sm font-bold rounded disabled:bg-slate-300">
          {saving ? '保存中...' : '保存する'}
        </button>
      )}
    </div>
  );
}


function getLatestFeedback(feedbacks) {
  if (!feedbacks || feedbacks.length === 0) return null;
  const latest = [...feedbacks].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
  return { verdict: latest.verdict, comment: latest.comment || null };
}

// =============================================
// 企業Admin画面（自社のユーザー・店舗・部門・使用量）
// =============================================
function CompanyAdminPanel({ onClose, onSignOut }) {
  const { profile, company } = useAuth();
  const signOut = onSignOut;
  const [tab, setTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [usage, setUsage] = useState([]);
  const [loading, setLoading] = useState(false);
  const [zoomImage, setZoomImage] = useState(null);

  // ユーザー招待
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviting, setInviting] = useState(false);

  // 設定カスタマイズ
  const [departmentLabels, setDepartmentLabels] = useState(null);
  const fetchDepartmentLabels = useCallback(async () => {
    if (!company) return;
    const { data } = await supabase.from('companies').select('department_labels').eq('id', company.id).single();
    setDepartmentLabels(data?.department_labels || null);
  }, [company]);
  useEffect(() => { fetchDepartmentLabels(); }, [fetchDepartmentLabels]);

  // 検査履歴
  const [inspHistory, setInspHistory] = useState([]);
  const [inspDateFrom, setInspDateFrom] = useState('');
  const [inspDateTo, setInspDateTo] = useState('');
  const [inspProductName, setInspProductName] = useState('');
  const [inspStatus, setInspStatus] = useState(['SAFE', 'DANGER', 'ERROR']);
  const [inspLoading, setInspLoading] = useState(false);
  const [bulkPdfLoading, setBulkPdfLoading] = useState(false);
  const { pendingPdf, shareOrDefer, sharePendingPdf } = usePendingPdfShare();
  const [selectedInsp, setSelectedInsp] = useState(null);

  const fetchUsers = useCallback(async () => {
    const { data } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('company_id', company.id)
      .order('created_at');
    setUsers(data || []);
  }, [company]);

  const fetchUsage = useCallback(async () => {
    const firstDay = jstMonthStart();
    const { data } = await supabase
      .from('pm_usage_daily')
      .select('date, match_count')
      .eq('company_id', company.id)
      .gte('date', firstDay)
      .order('date');
    setUsage(data || []);
  }, [company]);

  useEffect(() => {
    if (!company) return;
    setLoading(true);
    Promise.all([fetchUsers(), fetchUsage()])
      .finally(() => setLoading(false));
  }, [company, fetchUsers, fetchUsage]);

  const inviteUser = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    const { data: { session: currentSession } } = await supabase.auth.getSession();
    const res = await fetch('/api/invite', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${currentSession?.access_token}`,
      },
      body: JSON.stringify({
        email: inviteEmail.trim(),
        companyId: company.id,
        role: 'member',
        name: inviteName.trim() || inviteEmail.trim()
      })
    });
    const data = await res.json();
    setInviting(false);
    if (data.error) {
      alert('招待エラー: ' + data.error);
    } else {
      setInviteEmail('');
      setInviteName('');
      alert('招待メールを送信しました');
      fetchUsers();
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('このユーザーを削除しますか？Food-Label Assist（表示のチェック）からも削除されます。この操作は取り消せません。')) return;
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch('/api/delete-user', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
      body: JSON.stringify({ userId }),
    });
    const data = await res.json();
    if (!res.ok) { alert(data.error || 'ユーザーの削除に失敗しました'); return; }
    fetchUsers();
  };

  const updateUserRole = async (userId, newRole) => {
    // 管理者の上限は DB のトリガー（supabase/migrations/enforce_max_admin.sql）でも確かめる。ここでの確認は
    // 画面の一覧で先に知らせるためのもので、一覧が古い場合などは DB が弾き、その文言をそのまま出す
    if (newRole === 'admin' && company?.max_admin > 0) {
      const adminCount = users.filter(u => u.role === 'admin').length;
      if (adminCount >= company.max_admin) {
        alert(`管理者の上限（${company.max_admin}名）に達しています。`);
        return;
      }
    }
    const { error } = await supabase.from('user_profiles').update({ role: newRole }).eq('id', userId);
    if (error) { alert(error.hint === 'max_admin' ? error.message : 'ロール変更エラー: ' + error.message); fetchUsers(); return; }
    fetchUsers();
  };


  const fetchInspHistory = useCallback(async () => {
    setInspLoading(true);
    let query = supabase
      .from('pm_inspections')
      // model_used/device_type/browser_type/is_standaloneは開発者向けデータのため、
      // 企業Admin向けのこのクエリではそもそも取得しない（取得してから隠すのではなく
      // データ最小化する。superadmin向けは別クエリのため対象外）
      .select('id, serial, store_name, dept_name, lot_number, label_product_name, detected_food_visual, match_status, reason, inspected_at, user_email, image_mode, analyze_ms, app_version, image_urls, image_quality, inspection_feedbacks:pm_inspection_feedbacks(verdict, comment, created_at)')
      .eq('company_id', company.id)
      .order('inspected_at', { ascending: false })
      .limit(100);
    if (inspDateFrom) query = query.gte('inspected_at', inspDateFrom + 'T00:00:00+09:00');
    if (inspDateTo)   query = query.lte('inspected_at', inspDateTo   + 'T23:59:59+09:00');
    if (inspProductName.trim()) query = query.ilike('label_product_name', `%${inspProductName.trim()}%`);
    if (inspStatus.length && inspStatus.length < 3) query = query.in('match_status', inspStatus);
    const { data } = await query;
    setInspHistory(data || []);
    setInspLoading(false);
  }, [company, inspDateFrom, inspDateTo, inspProductName, inspStatus]);

  // 使用モデル・端末・ブラウザ・動作モードは開発者向けデータのため、企業Admin向けCSVには
  // 含めない（superadmin向けexportInspectionsCSVは対象外、従来通り含める）。この関数の
  // 列構成はApp.js側の一般ユーザー向けexportHistoryCSVと完全に一致させること
  const exportInspHistoryCSV = () => {
    const headers = ['シリアル', '店舗', '部門', 'ロット番号', '商品名(ラベル)', '目視識別', '判定', '判定理由', '検査日時', '担当者メール', '検査モード', 'フィードバック', 'FBコメント', '判定時間(秒)'];
    const rows = inspHistory.map(r => {
      const fb = getLatestFeedback(r.inspection_feedbacks);
      return [
        formatSerial(r.serial), r.store_name ?? '', r.dept_name ?? '', r.lot_number ?? '',
        r.label_product_name ?? '', r.detected_food_visual ?? '', r.match_status ?? '',
        r.reason ?? '',
        r.inspected_at ? new Date(r.inspected_at).toLocaleString('ja-JP') : '',
        r.user_email ?? '',
        r.image_mode === 'speed' ? '速度優先' : r.image_mode === 'accuracy' ? '精度優先' : '',
        fb ? FEEDBACK_LABEL[fb.verdict] ?? fb.verdict : '',
        fb?.comment ?? '',
        r.analyze_ms != null ? (r.analyze_ms / 1000).toFixed(2) : '',
      ];
    });
    const date = new Date().toISOString().slice(0, 10);
    exportCSV(headers, rows, `PM_検査履歴_${company.name}_${date}.csv`);
  };

  const monthlyTotal = usage.reduce((sum, r) => sum + r.match_count, 0);

  return (
    <div className="fixed inset-0 bg-white z-50 flex flex-col">
      {/* ヘッダー */}
      <header className="bg-indigo-700 text-white px-4 py-3 flex items-center gap-3">
        <button onClick={onClose} className="text-indigo-300 hover:text-white">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <div className="font-bold">{company?.name}</div>
          <div className="text-xs text-indigo-300">{profile?.name}（{profile?.role === 'admin' ? '管理者' : 'メンバー'}）</div>
        </div>
        <button onClick={signOut} className="text-xs text-indigo-300 hover:text-white flex items-center gap-1">
          <LogOut className="w-4 h-4" /> ログアウト
        </button>
      </header>

      {/* タブ */}
      <div className="flex border-b bg-white">
        {[
          { key: 'users', label: 'ユーザー', icon: Users },
          { key: 'usage', label: '使用量', icon: BarChart2 },
          { key: 'inspections', label: '検査履歴', icon: ClipboardList },
          // 「テスト」タブは隠し機能。user_profiles.allow_regression_tab をDBで
          // 明示的にtrueにしたユーザーにのみ表示する（SuperAdminは常時表示のため対象外）
          ...(profile?.allow_regression_tab ? [{ key: 'regression', label: 'テスト', icon: FlaskConical }] : []),
          { key: 'settings', label: '設定', icon: SlidersHorizontal },
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex-1 py-3 text-sm font-medium flex items-center justify-center gap-1.5 border-b-2 transition-colors ${
              tab === key ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto p-4 max-w-lg mx-auto w-full">
        {loading ? (
          <div className="text-center py-10 text-slate-400">読み込み中...</div>
        ) : (
          <>
            {/* ユーザー管理 */}
            {tab === 'users' && (
              <div className="space-y-4">
                <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                  <p className="text-xs font-bold text-slate-500">ユーザーを招待</p>
                  <input
                    type="email"
                    placeholder="メールアドレス"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    className="w-full p-2 border rounded text-sm"
                  />
                  <input
                    type="text"
                    placeholder="表示名（任意）"
                    value={inviteName}
                    onChange={(e) => setInviteName(e.target.value)}
                    className="w-full p-2 border rounded text-sm"
                  />
                  <button
                    onClick={inviteUser}
                    disabled={inviting || !inviteEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inviteEmail.trim())}
                    className="w-full py-2 bg-indigo-600 text-white text-sm font-bold rounded disabled:bg-slate-300"
                  >
                    {inviting ? '送信中...' : '招待メールを送る'}
                  </button>
                </div>

                <div className="space-y-1">
                  {users.map(u => (
                    <div key={u.id} className={`flex items-center justify-between p-2 rounded border ${u.is_active ? 'bg-white' : 'bg-slate-50 opacity-50'}`}>
                      <div>
                        <div className="text-sm font-medium">{u.name || '（名前未設定）'}</div>
                        <div className="text-xs text-slate-400">
                          {u.email && <span className="mr-1">{u.email}</span>}
                          {u.role === 'admin' ? '管理者' : u.role === 'superadmin' ? 'SuperAdmin' : 'メンバー'}
                          {!u.is_active && ' · 無効'}
                        </div>
                      </div>
                      {u.id !== profile?.id && u.role !== 'superadmin' && (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => updateUserRole(u.id, u.role === 'admin' ? 'member' : 'admin')}
                            className={`text-xs px-2 py-1 rounded border ${u.role === 'admin' ? 'bg-indigo-100 text-indigo-700 border-indigo-200' : 'bg-slate-100 text-slate-500 border-slate-200'}`}
                          >
                            {u.role === 'admin' ? '管理者' : 'メンバー'}
                          </button>
                          <button onClick={() => deleteUser(u.id)} className="text-slate-300 hover:text-red-500">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 店舗・部門管理 */}

            {/* 使用量 */}
            {tab === 'usage' && (
              <div className="space-y-4">
                <div className={`rounded-lg p-4 text-center ${company?.monthly_limit > 0 && monthlyTotal > company.monthly_limit ? 'bg-red-50' : 'bg-indigo-50'}`}>
                  <div className={`text-3xl font-bold ${company?.monthly_limit > 0 && monthlyTotal > company.monthly_limit ? 'text-red-700' : 'text-indigo-700'}`}>{monthlyTotal.toLocaleString()}</div>
                  <div className="text-sm text-slate-500 mt-1">今月の照合回数</div>
                  {company?.monthly_limit > 0 && (
                    <div className="mt-2">
                      <div className="text-xs text-slate-500">上限: {company.monthly_limit.toLocaleString()} 回</div>
                      <div className="w-full bg-slate-200 rounded-full h-2 mt-1">
                        <div className={`h-2 rounded-full ${monthlyTotal > company.monthly_limit ? 'bg-red-500' : 'bg-indigo-500'}`} style={{ width: `${Math.min((monthlyTotal / company.monthly_limit) * 100, 100)}%` }} />
                      </div>
                      {monthlyTotal > company.monthly_limit && (
                        <div className="text-xs text-red-600 font-bold mt-1">超過: {(monthlyTotal - company.monthly_limit).toLocaleString()} 回</div>
                      )}
                    </div>
                  )}
                  {(!company?.monthly_limit || company.monthly_limit === 0) && (
                    <div className="text-xs text-slate-400 mt-1">上限: 無制限</div>
                  )}
                </div>

                <div>
                  <p className="text-xs font-bold text-slate-500 mb-2">日別内訳（今月）</p>
                  {usage.length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-4">データなし</p>
                  ) : (
                    <div className="space-y-1">
                      {[...usage].reverse().map(row => (
                        <div key={row.date} className="flex items-center justify-between text-sm p-2 bg-white rounded border">
                          <span className="text-slate-600 font-mono text-xs">{row.date}</span>
                          <span className="font-bold text-indigo-700">{row.match_count.toLocaleString()} 回</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {tab === 'inspections' && (
              <div className="space-y-3">
                <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                  <p className="text-xs font-bold text-slate-500">検索条件（最大100件）</p>
                  <DateRangePicker
                    from={inspDateFrom}
                    to={inspDateTo}
                    onChange={({ from, to }) => { setInspDateFrom(from); setInspDateTo(to); }}
                  />
                  <input type="text" placeholder="品名で検索" value={inspProductName} onChange={e => setInspProductName(e.target.value)} className="w-full p-2 border rounded text-sm" />
                  <div className="flex gap-2 flex-wrap items-center">
                    <span className="text-xs text-slate-500">判定:</span>
                    {['SAFE', 'DANGER', 'ERROR'].map(level => (
                      <label key={level} className="flex items-center gap-1 text-sm cursor-pointer">
                        <input type="checkbox" checked={inspStatus.includes(level)} onChange={e => setInspStatus(prev => e.target.checked ? [...prev, level] : prev.filter(v => v !== level))} className="rounded" />
                        <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${
                          level === 'DANGER' ? 'bg-red-100 text-red-700' :
                          level === 'ERROR' ? 'bg-slate-100 text-slate-500' :
                          'bg-blue-100 text-blue-700'
                        }`}>{level}</span>
                      </label>
                    ))}
                  </div>
                  <button
                    onClick={fetchInspHistory}
                    disabled={inspLoading}
                    className="w-full py-2 bg-indigo-600 text-white text-sm font-bold rounded disabled:bg-slate-300 flex items-center justify-center gap-2"
                  >
                    <Search className="w-4 h-4" />
                    {inspLoading ? '検索中...' : '検索'}
                  </button>
                </div>

                {inspHistory.length > 0 && (
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={exportInspHistoryCSV}
                      className="w-full py-2 bg-green-600 hover:bg-green-500 text-white text-sm font-bold rounded flex items-center justify-center gap-2"
                    >
                      <Download className="w-4 h-4" /> CSV ({inspHistory.length}件)
                    </button>
                    <button
                      disabled={bulkPdfLoading}
                      onClick={async () => { setBulkPdfLoading(true); try { await openBulkInspectionPdf(inspHistory, { exporterEmail: profile?.email, onIOSPdfReady: shareOrDefer }); } finally { setBulkPdfLoading(false); } }}
                      className="w-full py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-bold rounded disabled:bg-slate-300 flex items-center justify-center gap-2"
                    >
                      <FileText className="w-4 h-4" /> {bulkPdfLoading ? '生成中...' : `PDF (${inspHistory.length}件)`}
                    </button>
                  </div>
                )}

                {/* 生成に時間がかかりユーザーアクティベーションが切れたときだけ出る。
                    navigator.shareはこのボタンのタップ自体を新しいアクティベーション
                    として使う（usePendingPdfShare参照） */}
                {pendingPdf && (
                  <button
                    onClick={sharePendingPdf}
                    className="w-full py-2 bg-amber-500 hover:bg-amber-400 text-white text-sm font-bold rounded flex items-center justify-center gap-2"
                  >
                    <FileText className="w-4 h-4" /> PDFを保存
                  </button>
                )}

                {inspHistory.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-4">
                    {inspLoading ? '' : '条件を指定して検索してください'}
                  </p>
                ) : (
                  <div className="space-y-1">
                    {inspHistory.map((insp, i) => {
                      const isOpen = selectedInsp === i;
                      return (
                        <div key={insp.serial ?? i} className="bg-white rounded border overflow-hidden">
                          <button className="w-full text-left p-2" onClick={() => setSelectedInsp(isOpen ? null : i)}>
                            <div className="flex items-center justify-between mb-0.5">
                              <div className="flex items-center gap-2 flex-wrap">
                                {insp.serial != null && (
                                  <span className="font-mono text-xs font-bold bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded">{formatSerial(insp.serial)}</span>
                                )}
                                <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${
                                  insp.match_status === 'DANGER' ? 'bg-red-100 text-red-700' :
                                  insp.match_status === 'ERROR'  ? 'bg-slate-100 text-slate-500' :
                                  'bg-blue-100 text-blue-700'
                                }`}>
                                  {insp.match_status === 'DANGER' ? <AlertTriangle className="w-3 h-3 inline mr-0.5" /> : insp.match_status === 'SAFE' ? <CheckCircle className="w-3 h-3 inline mr-0.5" /> : null}
                                  {insp.match_status}
                                </span>
                                {isBlurryImage(insp.image_quality) && (
                                  <span className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-500 text-white">
                                    <ImageOff className="w-3 h-3" aria-hidden="true" />{BLURRY_IMAGE_LABEL}
                                  </span>
                                )}
                                {insp.image_mode && (
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${IMAGE_MODE_STYLE[insp.image_mode] ?? 'bg-slate-100 text-slate-500'}`}>
                                    {IMAGE_MODE_LABEL[insp.image_mode] ?? insp.image_mode}
                                  </span>
                                )}
                                {(() => { const fb = getLatestFeedback(insp.inspection_feedbacks); return fb ? (
                                  <span title={fb.comment || ''} className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${FEEDBACK_STYLE[fb.verdict] ?? 'bg-slate-100 text-slate-500'}`}>
                                    {FEEDBACK_LABEL[fb.verdict] ?? fb.verdict}
                                  </span>
                                ) : null; })()}
                              </div>
                              <span className="text-xs text-slate-400 flex-shrink-0">
                                {new Date(insp.inspected_at).toLocaleString('ja-JP', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                            <div className="text-xs text-slate-500">{insp.store_name || '—'} · {insp.dept_name || '—'}</div>
                            <div className="text-xs text-slate-600 truncate">{insp.label_product_name || '—'}</div>
                          </button>
                          {isOpen && (
                            <div className="border-t p-2 bg-slate-50 space-y-2">
                              {insp.image_urls && insp.image_urls.length > 0 && (
                                <div className="space-y-1">
                                  <div className="flex gap-2 overflow-x-auto pb-1">
                                    {insp.image_urls.map((path, j) => (
                                      <StorageImage key={j} path={path} className="h-24 w-24 object-cover rounded border flex-shrink-0 cursor-pointer" alt={`img-${j}`} onClick={() => setZoomImage(path)} />
                                    ))}
                                  </div>
                                  <div className="flex gap-1">
                                    {insp.image_urls.map((path, j) => (
                                      <button key={j} onClick={() => downloadInspectionImage(path, insp.serial, j + 1)}
                                        className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 hover:bg-blue-100">
                                        DL {insp.serial}_{j + 1}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}
                              <div className="text-xs space-y-1 text-slate-600">
                                <div><span className="text-slate-400">メール:</span> {insp.user_email || '—'}</div>
                                <div><span className="text-slate-400">ロット:</span> {insp.lot_number || '—'}</div>
                                {insp.reason && <div><span className="text-slate-400">判定理由:</span> {insp.reason}</div>}
                                {insp.detected_food_visual && <div><span className="text-slate-400">目視識別:</span> {insp.detected_food_visual}</div>}
                                {insp.analyze_ms != null && (
                                  <div><span className="text-slate-400">判定時間:</span> <span className="font-mono">⏱ {(insp.analyze_ms / 1000).toFixed(2)}s</span></div>
                                )}
                                {/* 使用モデル・端末・動作モードは開発者向けデータのため企業Adminには表示しない
                                    （superadmin向けの同種表示は別ブロック、SuperAdminPanel.js、対象外） */}
                                {insp.app_version && (
                                  <div><span className="text-slate-400">バージョン:</span> <span className="font-mono">{insp.app_version}</span></div>
                                )}
                                {(() => { const fb = getLatestFeedback(insp.inspection_feedbacks); return fb ? (
                                  <div><span className="text-slate-400">フィードバック:</span>{' '}
                                    <span className={`inline px-1.5 py-0.5 rounded text-[10px] font-medium ${FEEDBACK_STYLE[fb.verdict] ?? ''}`}>{FEEDBACK_LABEL[fb.verdict] ?? fb.verdict}</span>
                                    {fb.comment && <span className="ml-2 text-slate-500">「{fb.comment}」</span>}
                                  </div>
                                ) : null; })()}
                              </div>
                              <div className="flex gap-2 flex-wrap mt-1">
                                {insp.image_urls?.length > 0 && (
                                  <button onClick={() => openInspectionPdf(insp, { exporterEmail: profile?.email, onIOSPdfReady: shareOrDefer })} className="text-[10px] px-2 py-1 rounded bg-slate-100 text-slate-600 hover:bg-slate-200 font-medium flex items-center gap-1">
                                    <FileText className="w-3 h-3" /> PDF
                                  </button>
                                )}
                                {insp.image_urls?.length > 0 && profile?.allow_regression_tab && (
                                  <RegisterReferenceButton inspection={insp} companyId={company.id} />
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {tab === 'regression' && profile?.allow_regression_tab && (
              <RegressionTestTab companies={[company]} />
            )}

            {tab === 'settings' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-xs font-bold text-slate-500 mb-2">部門名の表示カスタマイズ</h3>
                  <DepartmentLabelsEditor
                    companyId={company?.id}
                    currentLabels={departmentLabels}
                    canEdit={true}
                    onSaved={fetchDepartmentLabels}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </div>
      <ImageZoomOverlay path={zoomImage} onClose={() => setZoomImage(null)} />
    </div>
  );
}

export { CompanyAdminPanel, RegisterReferenceButton, fetchInspectionImages, imagesToHtml, openBlankTab, openTabWithContent, openInspectionPdf, openBulkInspectionPdf, buildInspectionCardHtml, getLatestFeedback };
